// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { post } from '@tls/auth';
import { useCallback, useRef, useState } from 'react';

const UPLOAD_PROGRESS_MAX = 99;

export default function useProjectImportApi() {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<number>();
  const [estimatedRemainingTime, setEstimatedRemainingTime] = useState<number>();
  const cancelledRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const uploadProject = useCallback(async (project: File) => {
    setUploading(true);
    setProgress(0);
    cancelledRef.current = false;
    abortControllerRef.current = new AbortController();

    const formData = new FormData();
    formData.append('project', project);
    //Add project content as readable stream

    try {
      return await post('projects/import', formData, false, false, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        signal: abortControllerRef.current.signal,
        onUploadProgress: progressEvent => {
          const normalizedProgress = Math.min(1, Math.max(0, progressEvent.progress ?? 0));
          setProgress(normalizedProgress * UPLOAD_PROGRESS_MAX);
          setEstimatedRemainingTime(progressEvent.estimated);
        },
      });
    } finally {
      setProgress(undefined);
      setEstimatedRemainingTime(undefined);
      setUploading(false);
      abortControllerRef.current = null;
    }
  }, []);

  const cancelImport = useCallback(() => {
    cancelledRef.current = true;
    abortControllerRef.current?.abort();
  }, []);

  return { uploading, uploadProject, cancelImport, cancelledRef, progress, estimatedRemainingTime };
}
